import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Box,
  Alert,
  Typography,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { pb, getCurrentUser } from "../services/pocketbase";

// Add the formatDiscordMarkdown function
const formatDiscordMarkdown = (text) => {
  if (!text) return "";

  // Process complex formats first
  // Underline Bold Italic: __***text***__
  text = text.replace(
    /__\*\*\*(.*?)\*\*\*__/g,
    "<u><strong><em>$1</em></strong></u>"
  );

  // Underline Bold: __**text**__
  text = text.replace(/__\*\*(.*?)\*\*__/g, "<u><strong>$1</strong></u>");

  // Underline Italic: __*text*__
  text = text.replace(/__\*(.*?)\*__/g, "<u><em>$1</em></u>");

  // Bold Italic: ***text***
  text = text.replace(/\*\*\*(.*?)\*\*\*/g, "<strong><em>$1</em></strong>");

  // Then process simpler formats
  // Bold: **text**
  text = text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

  // Italic: *text* or _text_
  text = text.replace(/\*(.*?)\*|_(.*?)_/g, "<em>$1$2</em>");

  // Underline: __text__
  text = text.replace(/__(.*?)__/g, "<u>$1</u>");

  // Strikethrough: ~~text~~
  text = text.replace(/~~(.*?)~~/g, "<s>$1</s>");

  // Code blocks: ```text```
  text = text.replace(/```(.*?)```/gs, "<pre><code>$1</code></pre>");

  // Inline code: `text`
  text = text.replace(/`(.*?)`/g, "<code>$1</code>");

  // Line breaks
  text = text.replace(/\n/g, "<br />");

  return text;
};

const ConductRuleEditor = ({ open, onClose, rule = null }) => {
  const [formData, setFormData] = useState({
    rule_number: "",
    rule_title: "",
    rule_content: "",
  });
  const [error, setError] = useState(null);
  const user = getCurrentUser();

  useEffect(() => {
    if (rule) {
      setFormData({
        rule_number: rule.rule_number || "",
        rule_title: rule.rule_title || "",
        rule_content: rule.rule_content || "",
      });
    } else {
      // For new rules, set default values
      setFormData({
        rule_number: "",
        rule_title: "",
        rule_content: "",
      });
    }
  }, [rule]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const data = {
        rule_number: parseInt(formData.rule_number, 10),
        rule_title: formData.rule_title,
        rule_content: formData.rule_content,
      };

      if (rule?.id) {
        // Update existing rule
        await pb.collection("conduct_rules").update(rule.id, data);

        // Send webhook for rule update
        try {
          await fetch("/api/webhooks/coc-logs", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              embeds: [
                {
                  title: "Code of Conduct Rule Updated",
                  color: 0xffa500, // Orange color
                  fields: [
                    {
                      name: "Updated By",
                      value: user?.username || "Unknown User",
                      inline: true,
                    },
                    {
                      name: "Timestamp",
                      value: new Date().toISOString(),
                      inline: true,
                    },
                    {
                      name: "Previous Rule",
                      value: `**Rule ${rule.rule_number}${
                        rule.rule_title ? `: ${rule.rule_title}` : ""
                      }**\n${rule.rule_content}`,
                    },
                    {
                      name: "Updated Rule",
                      value: `**Rule ${data.rule_number}${
                        data.rule_title ? `: ${data.rule_title}` : ""
                      }**\n${data.rule_content}`,
                    },
                  ],
                },
              ],
            }),
          });
        } catch (webhookError) {
          console.error(
            "Failed to send webhook for rule update:",
            webhookError
          );
        }
      } else {
        // For new rules, check if we need to auto-assign a rule number
        if (!formData.rule_number) {
          try {
            const records = await pb.collection("conduct_rules").getList(1, 1, {
              sort: "-rule_number",
            });

            if (records.items.length > 0) {
              data.rule_number = records.items[0].rule_number + 1;
            } else {
              data.rule_number = 1;
            }
          } catch (err) {
            console.error("Error getting highest rule number:", err);
            data.rule_number = 1;
          }
        }

        // Create new rule
        // eslint-disable-next-line
        const newRule = await pb.collection("conduct_rules").create(data);

        // Send webhook for new rule creation
        try {
          await fetch("/api/webhooks/coc-logs", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              embeds: [
                {
                  title: "New Code of Conduct Rule Created",
                  color: 0x00ff00, // Green color
                  fields: [
                    {
                      name: "Created By",
                      value: user?.username || "Unknown User",
                      inline: true,
                    },
                    {
                      name: "Timestamp",
                      value: new Date().toISOString(),
                      inline: true,
                    },
                    {
                      name: "New Rule",
                      value: `**Rule ${data.rule_number}${
                        data.rule_title ? `: ${data.rule_title}` : ""
                      }**\n${data.rule_content}`,
                    },
                  ],
                },
              ],
            }),
          });
        } catch (webhookError) {
          console.error("Failed to send webhook for new rule:", webhookError);
        }
      }

      onClose(true);
    } catch (err) {
      console.error("Error saving rule:", err);
      setError(err.message || "Failed to save rule");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="md"
      sx={{ zIndex: 99 }}
      fullWidth
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        {rule ? "Edit Rule" : "Add Rule"}
        <IconButton
          aria-label="close"
          onClick={() => onClose(false)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              label="Rule Number"
              type="number"
              value={formData.rule_number}
              onChange={(e) =>
                setFormData({ ...formData, rule_number: e.target.value })
              }
            />

            <TextField
              fullWidth
              label="Rule Title"
              value={formData.rule_title}
              onChange={(e) =>
                setFormData({ ...formData, rule_title: e.target.value })
              }
            />

            <Box>
              <TextField
                fullWidth
                label="Rule Content"
                value={formData.rule_content}
                onChange={(e) =>
                  setFormData({ ...formData, rule_content: e.target.value })
                }
                multiline
                rows={10}
                required
              />
              <FormHelperText>
                Use Discord-style markdown: **bold**, *italic*, ***bold
                italic***, __underline__, __*underline italic*__, __**underline
                bold**__, __***underline bold italic***__, ~~strikethrough~~,
                `inline code`, ```code blocks```
              </FormHelperText>

              {formData.rule_content && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    border: "1px solid #ccc",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Preview:
                  </Typography>
                  <Box
                    sx={{
                      whiteSpace: "pre-wrap",
                      "& code": {
                        backgroundColor: "rgba(0, 0, 0, 0.1)",
                        padding: "2px 4px",
                        borderRadius: "3px",
                        fontFamily: "monospace",
                      },
                      "& pre": {
                        backgroundColor: "rgba(0, 0, 0, 0.1)",
                        padding: "8px",
                        borderRadius: "4px",
                        overflowX: "auto",
                      },
                      "& strong": {
                        fontWeight: "bold",
                      },
                      "& em": {
                        fontStyle: "italic",
                      },
                      "& u": {
                        textDecoration: "underline",
                      },
                      "& s": {
                        textDecoration: "line-through",
                      },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: formatDiscordMarkdown(formData.rule_content),
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => onClose(false)}>Cancel</Button>
          <Button type="submit" variant="contained">
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ConductRuleEditor;
